import { io } from "socket.io-client";
import type { Socket } from "socket.io-client";
import { logger } from "./logger";
import { getVendorToken } from "./vendor-token";
import { SOCKET_EVENTS } from "@/shared/api/types.socket";
import {
  assessmentRoomsReadyEventSchema,
  followUpQuestionsGeneratedEventSchema,
  aiMessageEventSchema,
  followUpAnsweredStatusEventSchema,
} from "@/shared/api/schemas.socket";
import type {
  AssessmentRoomsReadyEventParsed,
  FollowUpQuestionsGeneratedEventParsed,
  AiMessageEventParsed,
  FollowUpAnsweredStatusEventParsed,
} from "@/shared/api/schemas.socket";
import { useSocketStore } from "@/stores/socket-store";

const MAX_RECONNECTION_ATTEMPTS = 3;

interface SocketEventHandlers {
  [SOCKET_EVENTS.ASSESSMENT_ROOMS_READY]: (
    data: AssessmentRoomsReadyEventParsed,
  ) => void;
  [SOCKET_EVENTS.FOLLOW_UP_GENERATED]: (
    data: FollowUpQuestionsGeneratedEventParsed,
  ) => void;
  [SOCKET_EVENTS.AI_MESSAGE]: (data: AiMessageEventParsed) => void;
  [SOCKET_EVENTS.ANSWERED_STATUS]: (
    data: FollowUpAnsweredStatusEventParsed,
  ) => void;
}
type SocketEventName = keyof SocketEventHandlers;

const eventSchemas = {
  [SOCKET_EVENTS.ASSESSMENT_ROOMS_READY]: assessmentRoomsReadyEventSchema,
  [SOCKET_EVENTS.FOLLOW_UP_GENERATED]: followUpQuestionsGeneratedEventSchema,
  [SOCKET_EVENTS.AI_MESSAGE]: aiMessageEventSchema,
  [SOCKET_EVENTS.ANSWERED_STATUS]: followUpAnsweredStatusEventSchema,
} as const;

class SocketManager {
  private socket: Socket | null = null;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  private wrappedHandlers = new Map<Function, Function>();
  private handleVisibilityChange: () => void;
  private handleOnline: () => void;

  private constructor() {
    this.handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        this.socket &&
        !this.socket.connected
      ) {
        logger.info("Tab became visible — reconnecting socket");
        this.socket.connect();
      }
    };

    this.handleOnline = () => {
      if (this.socket && !this.socket.connected) {
        logger.info("Network online — reconnecting socket");
        this.socket.connect();
      }
    };
  }

  connect(): void {
    const baseUrl =
      (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

    let serverUrl: string;
    let socketPath: string;

    // Parse baseUrl to separate origin from path prefix (for APIM routing)
    // The path prefix IS the socket.io path — the API gateway routes it to the socket server
    if (baseUrl) {
      const url = new URL(baseUrl);
      serverUrl = url.origin;
      socketPath = url.pathname.replace(/\/$/, "") || "/socket.io";
    } else {
      serverUrl = "";
      socketPath = "/socket.io";
    }

    // Disconnect stale socket if present but not connected
    if (this.socket?.connected) {
      return;
    }

    if (this.socket) {
      this.socket.disconnect();
    }

    this.socket = io(`${serverUrl}/vendor`, {
      path: socketPath,
      transports: ["websocket"],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: MAX_RECONNECTION_ATTEMPTS,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      auth: (cb: (data: { vendor_token: string }) => void) => {
        cb({ vendor_token: getVendorToken() ?? "" });
      },
    });

    const socketStore = useSocketStore.getState();

    this.socket.on("connect", () => {
      logger.info("Vendor socket connected");
      socketStore.setStatus("connected");
    });

    this.socket.on("disconnect", (reason) => {
      logger.info("Vendor socket disconnected", { reason });
      useSocketStore.getState().setStatus("disconnected");
    });

    this.socket.on("connect_error", (err) => {
      logger.error("Vendor socket connection error", { error: err.message });
      // Do not change status here — socket.io will retry automatically
    });

    this.socket.io.on("reconnect_attempt", (attempt) => {
      logger.info("Vendor socket reconnecting", { attempt });
      useSocketStore.getState().setStatus("reconnecting");
    });

    this.socket.io.on("reconnect_failed", () => {
      logger.error("Vendor socket reconnect failed — max attempts exhausted");
      useSocketStore.getState().setStatus("failed");
    });

    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("online", this.handleOnline);
  }

  disconnect(): void {
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    window.removeEventListener("online", this.handleOnline);
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  on<E extends SocketEventName>(
    event: E,
    handler: SocketEventHandlers[E],
  ): void {
    if (!this.socket) {
      logger.warn("SocketManager.on() called before connect()", { event });
      return;
    }
    const schema = eventSchemas[event];
    const wrapped = (raw: unknown) => {
      const result = schema.safeParse(raw);
      if (!result.success) {
        logger.error("Socket event validation failed", {
          event,
          issues: JSON.stringify(result.error.issues),
        });
        return;
      }
      (handler as (data: unknown) => void)(result.data);
    };
    this.socket.on(event as string, wrapped);
    this.wrappedHandlers.set(handler, wrapped);
  }

  off<E extends SocketEventName>(
    event: E,
    handler: SocketEventHandlers[E],
  ): void {
    if (!this.socket) {
      return;
    }
    const wrapped = this.wrappedHandlers.get(handler);
    if (wrapped) {
      this.socket.off(event as string, wrapped as (...args: unknown[]) => void);
      this.wrappedHandlers.delete(handler);
    }
  }

  static create(): SocketManager {
    return new SocketManager();
  }
}

let instance: SocketManager | null = null;

export function getSocketManager(): SocketManager {
  instance ??= SocketManager.create();
  return instance;
}
